// Shared "ranked plays" table used by the hint modal (pre-commit) and the
// tutor analysis modal (post-commit error/blunder). Both render the same
// rank · moves · win% · equity · loss columns; only the row interactions
// differ. Equity/loss are gammon-aware points equity (the ranking metric);
// Win% is derived from game equity.

import { Play } from "../engine/moves";
import { BAR } from "../engine/position";

export interface HintRow {
  play: Play;
  /** Gammon-aware points equity — the metric rows are ranked and diffed by. */
  equity: number;
  /** Probability of winning the game, 0..1. Undefined renders "—". */
  winProb?: number;
  /** 1-based rank shown in the first column. Use <= 0 to render the cell blank. */
  rank: number;
  isBest: boolean;
  isYours?: boolean;
}

export interface HintTableOpts {
  rows: HintRow[];
  /** Best points equity among all rows — used to compute each row's loss. */
  bestEquity: number;
  /** Fires when a row is clicked (after the "selected" highlight has been moved). */
  onSelect?: (row: HintRow, index: number) => void;
  /** If set, this row index is clicked once after the table is built. */
  defaultSelectIdx?: number;
}

/** Win probability from game equity: exact, since pWin + pLoss = 1. */
export function winProbFromGameEquity(gameEq: number): number | undefined {
  if (!Number.isFinite(gameEq)) return undefined;
  return (gameEq + 1) / 2;
}

export function buildHintTable(opts: HintTableOpts): HTMLTableElement {
  const table = document.createElement("table");
  table.className = "hint-table";

  // Two numeric columns side by side (Win% and Eq) are ambiguous without
  // labels — give the table a header row.
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  for (const [cls, label] of [["rank", ""], ["moves", "Move"], ["win", "Win%"], ["equity", "Eq"], ["loss", "Loss"]]) {
    const th = document.createElement("th");
    th.className = cls;
    th.textContent = label;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = document.createElement("tbody");
  table.appendChild(tbody);
  const trs: HTMLTableRowElement[] = [];

  opts.rows.forEach((r, i) => {
    const tr = document.createElement("tr");
    // The tutor list can skip ranks (#4, then your #14). Derive the jump from
    // the ranks themselves rather than taking a flag — the hint modal's ranks
    // are always contiguous, so this is inert there.
    const prev = opts.rows[i - 1];
    const rankGap =
      prev !== undefined && r.rank > 0 && prev.rank > 0 && r.rank > prev.rank + 1;
    tr.className = [
      "hint-row",
      r.isBest ? "best" : "",
      r.isYours ? "yours" : "",
      rankGap ? "rank-gap" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const rank = document.createElement("td");
    rank.className = "rank";
    rank.textContent = r.rank > 0 ? String(r.rank) : "";

    const moves = document.createElement("td");
    moves.className = "moves";
    const tags: string[] = [];
    if (r.isYours) tags.push("you");
    if (r.isBest) tags.push("best");
    moves.textContent = tags.length > 0 ? `${formatPlay(r.play)}  · ${tags.join(" · ")}` : formatPlay(r.play);

    const win = document.createElement("td");
    win.className = "win";
    win.textContent = r.winProb !== undefined ? `${Math.round(r.winProb * 100)}%` : "—";

    const equity = document.createElement("td");
    equity.className = "equity";
    equity.textContent = Number.isFinite(r.equity) ? formatEquity(r.equity) : "—";

    const loss = document.createElement("td");
    loss.className = "loss";
    const eqLoss = opts.bestEquity - r.equity;
    // The < 0.001 test collapses true ties (distinct finals with identical
    // equity) to "—" rather than showing a meaningless −0.000.
    loss.textContent = r.isBest
      ? "—"
      : !Number.isFinite(r.equity) || eqLoss < 0.001
        ? "—"
        : `−${eqLoss.toFixed(3)}`;

    tr.appendChild(rank);
    tr.appendChild(moves);
    tr.appendChild(win);
    tr.appendChild(equity);
    tr.appendChild(loss);

    tr.addEventListener("click", () => {
      for (const other of trs) other.classList.remove("selected");
      tr.classList.add("selected");
      opts.onSelect?.(r, i);
    });

    trs.push(tr);
    tbody.appendChild(tr);
  });

  if (
    opts.defaultSelectIdx !== undefined
    && opts.defaultSelectIdx >= 0
    && opts.defaultSelectIdx < trs.length
  ) {
    trs[opts.defaultSelectIdx].click();
  }

  return table;
}

function formatPlay(play: Play): string {
  if (play.length === 0) return "(no move)";
  return play.map((sm) => `${formatPoint(sm.from)}/${formatPoint(sm.to)}`).join(" ");
}

function formatPoint(idx: number): string {
  if (idx === BAR) return "bar";
  if (idx === -1) return "off";
  return String(idx + 1);
}

function formatEquity(eq: number): string {
  const v = eq.toFixed(2);
  return eq >= 0 ? `+${v}` : v;
}
